import { AxiosResponse } from 'axios';
import { Action } from 'redux';
import { ThunkAction } from 'redux-thunk';

import { PicklistValue } from 'components/inputs/types';
import { SyncariThunkDispatch } from 'hooks/redux';
import { get } from 'utils/AjaxUtil';
import { getErrorMessage } from 'utils/AppUtil';
import DataUrlConstants from 'utils/DataUrlConstants';
import { makeUrl } from 'utils/UrlUtil';

import { RootState } from '../../reducers';
import {
  FetchPicklistValuesBatchComplete,
  FetchPicklistValuesBatchPending,
  GetPicklistValues,
  GetPicklistValuesFailed,
  GetPicklistValuesFulfilled,
} from './actions';

export function getPicklistValues(picklistParams: any): ThunkAction<void, RootState, unknown, Action<string>> {
  return (dispatch: SyncariThunkDispatch) => {
    dispatch(GetPicklistValues());
    get(
      makeUrl(DataUrlConstants.GET_PICKLIST_VALUES, undefined, {
        ...(picklistParams?.params || {}),
        dependantId: picklistParams.dependantId,
        dependantType: picklistParams.dependantType,
        sendPicklistGroup: picklistParams.sendPicklistGroup,
        ...(picklistParams.context && { context: picklistParams.context }),
      })
    )
      .then((resp) => {
        dispatch(GetPicklistValuesFulfilled(picklistParams.key, resp.data));
      })
      .catch((error) => {
        dispatch(GetPicklistValuesFailed(getErrorMessage(error)));
      });
  };
}

export interface FetchPicklistValuesParams {
  id: string;
  dependantId: string;
  dependantType: string;
  sendPicklistGroup?: boolean;
  context?: string;
  params?: Record<string, string>;
}

export type FetchPicklistValuesResponse = PicklistValue[];

// Cache to prevent duplicate API calls for the same picklist
const picklistsInFlight = new Map<string, Promise<AxiosResponse<FetchPicklistValuesResponse>>>();

// Auto-batching infrastructure
interface AutoBatchRequest {
  picklistId: string;
  params: FetchPicklistValuesParams;
  resolve: (value: AxiosResponse<FetchPicklistValuesResponse>) => void;
  reject: (reason: any) => void;
}

const autoBatchQueue = new Map<string, AutoBatchRequest>();
let autoBatchTimeout: NodeJS.Timeout | null = null;

// Processes all queued picklist requests in a single batch operation.
// Receives dispatch explicitly rather than pulling it from the queue.
async function processAutoBatchedRequests(dispatch: SyncariThunkDispatch) {
  if (autoBatchQueue.size === 0) {
    autoBatchTimeout = null;
    return;
  }

  const requestsToProcess = Array.from(autoBatchQueue.values());
  autoBatchQueue.clear();
  autoBatchTimeout = null;

  const ids = requestsToProcess.map(({ picklistId }) => picklistId);
  dispatch(FetchPicklistValuesBatchPending(ids));

  // Let Promise.allSettled detect rejections naturally — no inner try/catch needed
  const results = await Promise.allSettled(
    requestsToProcess.map(({ picklistId, params }) =>
      get<FetchPicklistValuesResponse>(
        makeUrl(DataUrlConstants.GET_PICKLIST_VALUES, undefined, {
          ...(params?.params || {}),
          dependantId: params.dependantId,
          dependantType: params.dependantType,
          sendPicklistGroup: params.sendPicklistGroup,
          ...(params.context && { context: params.context }),
        })
      ).then((response) => ({ picklistId, response }))
    )
  );

  const successfulResults: Array<{ id: string; pickListValues: PicklistValue[] }> = [];
  const failedResults: Array<{ id: string; error: any }> = [];

  results.forEach((result, index) => {
    const { picklistId, resolve, reject } = requestsToProcess[index];
    if (result.status === 'fulfilled') {
      resolve(result.value.response);
      successfulResults.push({ id: picklistId, pickListValues: result.value.response.data });
    } else {
      reject(result.reason);
      failedResults.push({ id: picklistId, error: getErrorMessage(result.reason) });
    }
  });

  // Single dispatch covers both successes and failures atomically
  dispatch(FetchPicklistValuesBatchComplete(successfulResults, failedResults));

  requestsToProcess.forEach(({ picklistId }) => {
    picklistsInFlight.delete(picklistId);
  });
}

/** @internal Resets module-level batch state between tests. Not for production use. */
export function _resetBatchStateForTesting() {
  picklistsInFlight.clear();
  autoBatchQueue.clear();
  if (autoBatchTimeout !== null) {
    clearTimeout(autoBatchTimeout);
    autoBatchTimeout = null;
  }
}

/**
 * Fetches picklist values with automatic batching and deduplication.
 *
 * This function implements two key optimizations:
 * 1. Automatic batching: Multiple calls within the same event loop tick are collected and executed
 *    as a single batch, reducing Redux dispatches from N to 3 (pending, fulfilled, cleanup) regardless
 *    of how many picklists are requested, keeping the UI smooth during heavy loads.
 * 2. Request deduplication: If the same picklist is requested multiple times, only one API call is made
 *    and all callers share the same promise, preventing redundant network requests.
 *
 * Works transparently with both individual and bulk requests - no code changes needed in consumers.
 */
export function fetchPicklistValues(
  params: FetchPicklistValuesParams
): ThunkAction<void, RootState, unknown, Action<string>> {
  return async (dispatch) => {
    const picklistId = params.id;
    if (!picklistsInFlight.has(picklistId)) {
      const batchPromise = new Promise<AxiosResponse<FetchPicklistValuesResponse>>((resolve, reject) => {
        autoBatchQueue.set(picklistId, { picklistId, params, resolve, reject });

        if (autoBatchTimeout === null) {
          // Capture dispatch in the closure so processAutoBatchedRequests
          // receives it as an explicit argument rather than pulling it from the queue
          autoBatchTimeout = setTimeout(() => processAutoBatchedRequests(dispatch), 0);
        }
      });

      picklistsInFlight.set(picklistId, batchPromise);
    }

    try {
      await picklistsInFlight.get(picklistId)!;
    } catch (error) {
      // Error already handled in processAutoBatchedRequests
    }
  };
}
